import { Theme, css, useTheme } from '@emotion/react';

import { useRouter } from 'next/router';

import { useCallback } from 'react';

import { FontSizes, Radius, Sizes, Spacing } from 'chatie-design-system';
import { Close, Icon } from 'chatie-design-system/icon';

import { ITag } from '../../model';
import { useAnalytics } from '../../utils/analytics';
import { OnClickProps, toQueryString } from '../../utils/misc';
import { useWebView } from '../../utils/webview';

const tagCSS = (theme: Theme) => css`
  display: inline-block;
  padding: 0 ${Spacing.xsmall}px;
  height: 36px;
  border: 1px solid ${theme.border.light};
  border-radius: ${Radius.small}px;
  margin-top: ${Spacing.xsmall}px;
  margin-right: ${Spacing.xsmall}px;
  font-size: ${FontSizes.small}px;

  span::before {
    content: '#';
  }

  i {
    margin-left: ${Spacing.xxsmall}px;
  }

  &:last-of-type {
    margin-right: 0;
  }
`;

interface TagProps extends ITag {
  edit?: boolean;
}

export const Tag = ({ id, name, edit, onClick }: TagProps & OnClickProps) => {
  const theme = useTheme();

  return (
    <button
      css={tagCSS(theme)}
      type="button"
      className="icon-text"
      data-id={id}
      data-name={name}
      onClick={onClick}
    >
      <span>{name}</span>
      {edit && (
        <Icon icon={Close} size={Sizes.small} color={theme.var.gray500} />
      )}
    </button>
  );
};

const tagListCSS = (isScrollable: boolean) => css`
  word-break: keep-all;

  ${isScrollable &&
  css`
    white-space: nowrap;
    overflow: auto;
    padding: 0 ${Spacing.medium}px;
    margin: 0 -${Spacing.medium}px;
  `}
`;

type Props = {
  tags: ITag[];
  scroll?: boolean;
  webView?: boolean;
};

export const TagList = ({ tags, webView, scroll = false }: Props) => {
  const router = useRouter();
  const { web } = useWebView();
  const { firebase } = useAnalytics();

  const handleClick = useCallback(
    (tag: ITag) => {
      const url = `/tags/${tag.id}/stories${toQueryString({ name: tag.name })}`;
      if (webView) {
        web.openPage(url);
      } else {
        router.push(url);
      }

      firebase.logEvent('select_tag', {
        tagId: tag.id,
        tagName: tag.name,
        from: router.asPath,
      });
    },
    [webView]
  );

  return (
    <div css={tagListCSS(scroll)}>
      {tags.map((tag) => (
        <Tag key={`tag-${tag.id}`} {...tag} onClick={() => handleClick(tag)} />
      ))}
    </div>
  );
};

export default Tag;
